import type { BasketState } from '../ducks/common/basket';
import type { TimeSlotVersion } from '../ducks/customer/types';
import type { GeneratedPotential } from '../types/basket';
import type { CancelReasonValue } from '../types/cancel-reasons';
import type { PrestationReference, ProChoiceSource, ProGender, UniverseReference } from '../types/commons';
import type { Booking } from '../types/customer';
import { del, get, post, put } from './requester';

type BookingCancelArgs = {
  publicId: string;
  reason: string;
  reasonDetail?: string;
  shouldCancelAllRecurrences?: boolean;
};

export const orders = {
  createMessage: (orderId: string, message: string, image?: Blob) => {
    const data = new FormData();
    if (message) data.append('message', message);
    if (image) data.append('image', image as any);
    return post(`/customer/orders/${orderId}/messages`, { body: data, isFormData: true });
  },
  get: (publicId: string) => get(`/customer/orders/${publicId}`),
  cancel: ({ publicId, reason, reasonDetail = undefined, shouldCancelAllRecurrences = undefined }: BookingCancelArgs) =>
    del(`/customer/orders/${publicId}/cancel`, {
      body: {
        reason,
        reason_detail: reasonDetail,
        all_potential_orders: shouldCancelAllRecurrences,
      },
    }),
  createChangeRequest: (publicId: string, potential: GeneratedPotential) =>
    post(`/customer/orders/${publicId}/change_requests`, { body: { potential } }),
  getMessages: (publicId: string) => get(`/customer/orders/${publicId}/messages`),
  markMessagesAsRead: (publicId: string) => put(`/customer/orders/${publicId}/messages/mark_as_read`),
  giveTip: (publicId: string, amount: number) => post(`/customer/orders/${publicId}/tip`, { query: { amount } }),
};

export type FindNewProArgs = {
  // TODO TI Booking: Better use Order['potential_public_id']
  publicId: Booking['public_id'];
  cancellationReason: CancelReasonValue;
  reasonDetail?: string;
  origin?: string;
};

export type UpdatePotentialProGenderArgs = {
  // TODO TI Booking: Better use Order['potential_public_id']
  publicId: Booking['public_id'];
  proGender: Exclude<ProGender, 'dont_care'>;
};

export const potentials = {
  get: (publicId: string) => get(`/customer/potentials/${publicId}`),
  cancel: ({ publicId, reason, reasonDetail = undefined }: BookingCancelArgs) =>
    del(`/customer/potentials/${publicId}/cancel`, {
      body: {
        reason,
        reason_detail: reasonDetail,
      },
    }),
  getMinAppointment: (zip_code: string, universe: UniverseReference) =>
    get('/customer/potentials/min_appointment', { query: { zip_code, universe } }),
  validate: (potential: GeneratedPotential, skipSetupIntentCreation: boolean) =>
    post('/customer/potentials/validate', {
      body: { potential, skip_setup_intent_creation: skipSetupIntentCreation },
    }),
  create: (potential: GeneratedPotential) => post('/customer/potentials', { body: { potential } }),
  update: (publicId: string, potential: Partial<GeneratedPotential>) =>
    put(`/customer/potentials/${publicId}`, { body: { potential } }),
  acceptOtherPros: (publicId: string) => put(`/customer/potentials/${publicId}/exclusive_accept_other_pros`),
  findNewPro: ({ publicId, cancellationReason, reasonDetail, origin }: FindNewProArgs) =>
    put(`/customer/potentials/${publicId}/find_new_pro`, {
      body: {
        cancellation_reason: cancellationReason,
        cancellation_reason_detail: reasonDetail,
        origin,
      },
    }),
  getMessages: (publicId: string) => get(`/customer/potentials/${publicId}/messages`),
  markMessagesAsRead: (publicId: string) => put(`/customer/potentials/${publicId}/messages/mark_as_read`),
  updatePotentialProGender: ({ publicId, proGender }: UpdatePotentialProGenderArgs) =>
    put(`/customer/potentials/${publicId}/pro_gender`, {
      body: {
        pro_gender: proGender,
      },
    }),
};

type TimeSlotsGetProps = {
  zipCode: string;
  universe: UniverseReference;
  date?: string;
  prestations?: string[];
  proGender?: ProGender;
  exclusivePro: boolean;
  proChoiceSources: ProChoiceSource[];
  weeklyRecurrence?: number;
  timeSlotVersion?: TimeSlotVersion;
};

export const timeSlots = {
  get: ({
    zipCode,
    universe,
    date,
    prestations,
    proGender,
    exclusivePro,
    proChoiceSources,
    weeklyRecurrence,
    timeSlotVersion,
  }: TimeSlotsGetProps) =>
    get('/customer/time_slots', {
      query: {
        zip_code: zipCode,
        universe,
        date,
        prestations,
        pro_gender: proGender,
        exclusive_pro: exclusivePro,
        pro_choice_sources: proChoiceSources,
        weekly_recurrence: weeklyRecurrence,
        algo_version: timeSlotVersion,
      },
    }),
};

type GetProChoicesArgs = {
  universe: UniverseReference;
  zip_code: string;
  lat: string | number;
  lng: string | number;
  prestations: PrestationReference[];
  pro_gender?: ProGender;
  weekly_recurrence?: number;
  duration?: number;
};

type GetPreviousProsArgs = {
  universe: UniverseReference;
  zipCode: string;
  referringPro?: string;
  prestations?: PrestationReference[];
  weeklyRecurrence?: BasketState['weekly_recurrence'];
  duration?: BasketState['duration'];
};

export const proAccounts = {
  get: () => get('/customer/pro_accounts'),
  getPrevious: ({ universe, zipCode, referringPro, prestations, weeklyRecurrence, duration }: GetPreviousProsArgs) =>
    get('/customer/pro_accounts/previous', {
      query: {
        universe,
        zip_code: zipCode,
        referring_pro: referringPro,
        prestations,
        weekly_recurrence: weeklyRecurrence,
        duration,
      },
    }),
  checkAvailability: (universe: UniverseReference, zip_code?: string, lat?: string | number, lng?: string | number) =>
    get('/customer/pro_accounts/check_availability', { query: { universe, zip_code, lat, lng } }),
  getProChoices: ({
    universe,
    zip_code,
    lat,
    lng,
    prestations,
    pro_gender,
    duration,
    weekly_recurrence,
  }: GetProChoicesArgs) =>
    get('/customer/pro_accounts/pro_choice_selection', {
      query: {
        universe,
        zip_code,
        prestations: prestations.join(','),
        pro_gender,
        lat,
        lng,
        duration,
        weekly_recurrence,
      },
    }),
  getPreviousProsReviews: (universe: UniverseReference, proId: string, pageNumber: number) =>
    get(`/customer/pro_accounts/${proId}/order_reviews`, {
      query: { universe, 'page[size]': 4, 'page[number]': pageNumber },
    }),
};
